import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { LinkedPanel } from "../../../components/PortfolioPerformance/linked-panel";
import { Skeleton } from "../../../ui/skeleton";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import {
  useEarningsProjections,
  useEarningsReturn,
  type ProjectionPoint,
} from "../lib/api/earnings-return";

function CardSpark({
  data,
  color = "var(--amber)",
}: {
  data: { index: number; value: number }[];
  color?: string;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        {/* Fit the series, not 0 → max, so the movement is visible (same as Loss & Recovery). */}
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          fill={color}
          fillOpacity={0.15}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function ProjectionPanel({
  title,
  description,
  points,
  loading,
  failed,
  seriesName,
  color,
}: {
  title: string;
  description: string;
  points: ProjectionPoint[] | null | undefined;
  loading: boolean;
  failed: boolean;
  seriesName: string;
  color: string;
}) {
  return (
    <Panel title={title} description={description}>
      {failed ? (
        <p className="text-sm text-destructive">Could not load this chart.</p>
      ) : loading ? (
        <Skeleton className="h-60 w-full" />
      ) : !points || points.length === 0 ? (
        <div className="flex h-60 items-center justify-center text-sm text-muted-foreground">
          Not Available Yet.
        </div>
      ) : (
        <TrendLineChart
          data={points}
          series={[{ dataKey: "value", name: seriesName, color }]}
          currency="millions"
          height={240}
        />
      )}
    </Panel>
  );
}

export default function EarningsReturnPage() {
  const { data, isPending, isError, error } = useEarningsReturn();
  const projections = useEarningsProjections();

  return (
    <div>
      <PageHeader
        title="Earnings & Return"
        subtitle="Given the risk already priced and absorbed, what the portfolio is actually earning: repeat business, prepayments, chargeoffs, and lease income in one view."
        asOfPage="earnings-return"
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Earnings & return data could not be read.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending ? (
        <div className="mt-8 grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : data ? (
        <>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Net Investment
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.netInvestment}
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Unearned Interest
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.unearnedInterest}
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Earned Interest (YTD)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.earnedInterest}
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Risk-Adjusted Yield
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.riskAdjustedYield.current}
              </p>
              <p
                className={`mt-1 text-xs font-medium tabular-nums ${
                  data.riskAdjustedYield.trend.direction === "down"
                    ? "text-decline"
                    : data.riskAdjustedYield.trend.direction === "up"
                      ? "text-approve"
                      : "text-muted-foreground"
                }`}
              >
                {data.riskAdjustedYield.trend.direction === "down"
                  ? "▼"
                  : data.riskAdjustedYield.trend.direction === "up"
                    ? "▲"
                    : "—"}{" "}
                {data.riskAdjustedYield.trend.value}
                {data.riskAdjustedYield.comparison ? (
                  <span className="ml-1 font-normal text-muted-foreground">{data.riskAdjustedYield.comparison}</span>
                ) : null}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ProjectionPanel
              title="Expected Repeat Customers"
              description="Expectation over next 60 months"
              points={projections.data?.expectedRepeatCustomers}
              loading={projections.isPending}
              failed={projections.isError}
              seriesName="Expected Repeat Customers"
              color="hsl(var(--chart-2))"
            />
            <ProjectionPanel
              title="Expected Prepayments"
              description="Expectation over next 60 months"
              points={projections.data?.expectedPrepayments}
              loading={projections.isPending}
              failed={projections.isError}
              seriesName="Expected Prepayments"
              color="hsl(var(--chart-3))"
            />
            <ProjectionPanel
              title="Expected Loss - Chargeoffs"
              description="Expectation over next 60 months"
              points={projections.data?.expectedLossChargeoffs}
              loading={projections.isPending}
              failed={projections.isError}
              seriesName="Expected Loss - Chargeoffs"
              color="var(--decline)"
            />
            <Panel
              title="Lease Income Projections"
              description="Expectation over next 60 months"
            >
            <TrendLineChart
              data={data.earningsTrend}
              series={[
                {
                  dataKey: "totalEarningsProj",
                  name: "Total Earnings Proj.",
                  color: "var(--amber)",
                },
                {
                  dataKey: "repeatCustomerEarnings",
                  name: "Repeat Customer Earnings",
                  color: "hsl(var(--chart-2))",
                },
                {
                  dataKey: "chargeoffEarningsLoss",
                  name: "Chargeoff Earnings Loss",
                  color: "var(--decline)",
                },
                {
                  dataKey: "prepaymentEarningsLoss",
                  name: "Prepayment Earnings Loss",
                  color: "hsl(var(--chart-3))",
                },
              ]}
              currency="millions"
              height={240}
            />
            </Panel>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LinkedPanel
              to="/portfolio-performance/lifetime-prepayment-projections"
              title="Prepayment Projections"
              statLabel="Trailing Prepayment Rate"
              stat={data.cards.prepayment.stat}
              chart={<CardSpark data={data.cards.prepayment.sparkline} color="hsl(var(--chart-3))" />}
              footnote="Unrealized interest income at risk from early payment: principal recovered, income lost."
            />
            <LinkedPanel
              to="/portfolio-performance/lifetime-repeat-customer-projections"
              title="Repeat Customer Projections"
              statLabel={`${data.cards.repeatCustomer.windowMonths ?? 48}-Month Repeat Rate`}
              stat={data.cards.repeatCustomer.stat}
              trend={data.cards.repeatCustomer.trend ?? undefined}
              chart={
                <CardSpark data={data.cards.repeatCustomer.sparkline} color="hsl(var(--chart-2))" />
              }
              footnote={`Repeat-business rate at ${data.cards.repeatCustomer.windowMonths ?? 48} months, latest file date; change is vs the previous file date.`}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
